<template>
  <div class="home">
    <HeaderComponent screenName=""/>
    <!-- Business Banner -->
    <BannerComponent
      imageSrc="capabilities/devsecops/devsecops-banner.jpg"
      :bannerHeadingColor="'#fff'"
      :isOverlay="true"
      heading="DevSecOps"
      subheading="Where Development, Security, and Operations Join in Synchronicity"
    />
    <div class="modernization-description" data-aos="fade-up">
      <v-row>
        <v-col
          :xs="12"
        >
          <p> {{ appModernizationDesc }} </p>
        </v-col>
      </v-row>
    </div>
    <CardComponent
      :subHeading="`BENEFITS`"
      :cardItems="benefitsItems"
    />
    <CardComponent 
      :subHeading="`CORE CAPABILITIES`"
      :cardItems="coreCapabilitiesItems"
    />
    <SectionComponent
       relatedTitle="RELATED CAPABILITIES"
       paragraphContent="As a leading DevSecOps service provider with an exemplary track record, our team of experts have successfully modernized systems and applications for the Federal, State and Local governments, commercial customers, and non-profits. Our DevSecOps services are utilized by our customers for several compelling reasons, as we offer numerous benefits that address the challenges and requirements of modern software development and deployment. Our successes providing DevSecOps capabilities include enhancing security, faster delivery times, cost reduction, improving collaboration, greater scalability, and compliance and governance for meeting regulatory requirements."
      :sections="sectionContents"
      :cards="cardData"
      :additional-content="additionalContent"
    />
    <FooterComponent />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, Ref } from 'vue';

// Components
import HeaderComponent from '@/components/HeaderComponent.vue';
import BannerComponent from '@/components/BannerComponent.vue';
import CardComponent from '@/components/CardComponent.vue';
import SectionComponent from '@/components/SectionComponent.vue';
import FooterComponent from '@/components/FooterComponent.vue';

export default defineComponent({
  components: {
    HeaderComponent,
    BannerComponent,
    CardComponent,
    SectionComponent,
    FooterComponent
  },
  data() {
    return {
      appModernizationDesc: 'G14C DevSecOps services provides customers with the capabilities for building, deploying, and managing applications, services, and infrastructure for modernizing systems and applications. We utilize best practices and principles to integrate security into the development and operations process to rapidly and continuously develop and deploy applications and services for our customers’ systems and applications. Our approach emphasizes collaboration between software developers, IT professionals, and product owners to automate the process of software delivery and infrastructure changes reliably. Our approach incorporates security throughout the entire software development lifecycle.',
      benefitsItems: [
        {
          id: 1,
          subFolder:'capabilities', 
          folder:'devsecops',
          imageUrl: 'Delivery Time.jpg',
          text: 'Faster Delivery Time',
          hoverIcon: 'vector.png',
          hoverText: 'Reducing delivery times and costs by automating security processes,  improving collaboration, leveraging CI/CD automation tools, and utilizing modern DevSecOps technology.'
        },{
          id: 2,
          subFolder:'capabilities',   
          folder:'devsecops',
          imageUrl: 'Collaboration.jpg',
          text: 'Improve Collaboration',
          hoverIcon: 'chart.png',
          hoverText: 'Our DevSecOps approach encourages collaboration between development, operations, and security teams to foster a better understanding of requirements and ensures that security is everyones responsibility.'
        },{
          id: 3,
          subFolder:'capabilities',  
          folder:'devsecops',
          imageUrl: 'security.jpg',
          text: 'Robust Security',
          hoverIcon: 'trend-down.png',
          hoverText: 'Enhances security by incorporating security practices throughout the development and deployment lifecycle to identify and address vulnerabilities and threats early, reducing the risk of security issues.'
        },{
          id: 4,
          subFolder:'capabilities',  
          folder:'devsecops',
          imageUrl: 'Scalability.jpg',
          text: 'Greater Scalability',
          hoverIcon: 'lock.png',
          hoverText: 'Our DevSecOps practices are highly scalable scaled to handle complex and large-scale projects, making it suitable for large enterprise modernization initiatives.'
        }
      ],
      
      coreCapabilitiesItems: [
        {
          id: 5,
          subFolder:'capabilities',  
          folder:'devsecops',
          imageUrl: 'Automation.jpg',
          text: 'Automation',
          hoverIcon: 'analysis.png',
          hoverText: 'Automating development and operations processes and security controls including automated vulnerability scanning, code analysis, compliance checks, and security testing to ensure that security is synchronized with the pace of CI/CD pipelines.'
        },{
          id: 6,
          subFolder:'capabilities',  
          folder:'devsecops',
          imageUrl: 'Integration.jpg',
          text: 'Continuous Integration',
          hoverIcon: 'inventory.png',
          hoverText: 'Continuously integrating code changes from multiple contributors into a shared repository to perform automated security tests, static code analysis and automated testing, to identify and address vulnerabilities as code is developed.'
        },{
          id: 7,
          subFolder:'capabilities',  
          folder:'devsecops',
          imageUrl: 'Delivery.jpg',
          text: 'Continuous Delivery',
          hoverIcon: 'ai-brain.png',
          hoverText: 'Automating the build, test and deployment process for various environments, from development to production while integrating security checks into the pipelines to ensure that only secure code is deployed.'
        },{
          id: 8,
          subFolder:'capabilities',  
          folder:'devsecops',
          imageUrl: 'container-security.jpg',
          text: 'Container Security',
          hoverIcon: 'cloud-done.png',
          hoverText: 'Our DevSecOps approach includes container security practices that involve scanning container images for vulnerabilities, applying access controls, and monitoring containers in production for any security threats.'
        },{
          id: 9,
          subFolder:'capabilities',  
          folder:'devsecops',
          imageUrl: 'code.jpg',
          text: 'Infrastructure as Code ',
          hoverIcon: 'software-license.png',
          hoverText: 'Our DevSecOps approach treats infrastructure as code allowing for security controls and configurations to be defined in code, under version-control, and tested alongside application code to ensure that the infrastructure is secure.'
        },{
          id: 10,
          subFolder:'capabilities',  
          folder:'devsecops',
          imageUrl: 'Threat.jpg',
          text: 'Threat Modeling',
          hoverIcon: 'microservices.png',
          hoverText: 'Our DevSecOps approach emphasizes threat modeling as a practice to identify and mitigate security threats during the design and planning phases to proactively prevent vulnerabilities from being introduced.'
        }
      ],
      sectionContents: [
        '“The contractor was responsible for the continuous delivery of enhanced application functionality to selected infrastructure environments, such as development, quality assurance, staging, and production ensuring that changes to code are delivered/pushed to the appropriate environments, utilizing DevSecOps and CI/CD tools. The team did an excellent job communicating across the multiple contractor teams in our environment. The contractor often stepped up to lead efforts that required coordination among all the contractor teams, going above and beyond what was expected.”',
        '“The contractor is responsible for providing Development, Modernization, and Enhancements, Operations and Maintenance, and Service Desk services for FDA ESG system. The team utilizes and Agile approach for service delivery, and utilizes HCD/UXD design principles for the user experience. The contractor performs DevSecOps for system design and development activities, and they are responsible for AWS cloud migration and adoption for ESG. The technical team is very good at developing and implementing solutions for keeping up with FDA IT standards and internal technological challenges.”',
      ],
      additionalContent: [
        'SEC, Assistant Director', 
        'FDA, CO', 
      ],
      cardData: [
        {
          className: 'devsecops',
          color: '#7594FE',
          heading: 'Application Modernization	',
          link: 'application-modernization'
        },
        {
          className: 'cloud',
          color: '#50E1FE',
          heading: 'Cloud',
          link: 'cloud'
        },
        {
          className: 'security',
          color: '#E2BF66',
          heading: 'Cybersecurity',
          link: 'cyber-security'
        },
      ]
    };
  },
  setup() {
    return {
    };
  },
});
</script>
<style scoped>
.modernization-description{
  padding: 32px;
  padding-left: 100px;
  padding-right: 100px;
}
.modernization-description div div p{
  font-size: 24px;
}
@media (max-width: 1000px) {
  .modernization-description{
    padding: 27px;
    padding-left: 80px;
    padding-right: 80px;
  }
  .modernization-description div div p{
    font-size: 24px;
  }
}
@media (max-width: 850px) {
  .modernization-description{
    padding: 22px;
    padding-left: 65px;
    padding-right: 65px;
  }
  .modernization-description div div p{
    font-size: 22px;
  }
}
@media (max-width: 750px) {
  .modernization-description{
    padding: 17px;
    padding-left: 50px;
    padding-right: 50px;
  }
  .modernization-description div div p{
    font-size: 20px;
  }
}
@media (max-width: 600px) {
  .modernization-description{
    padding: 12px;
    padding-left: 35px;
    padding-right: 35px;
  }
  .modernization-description div div p{
    font-size: 18px;
  }
}
</style>